import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useServices } from '@/context/ServiceContext'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import type { Certificate } from '@/domain/certificate'
import type { AppUser } from '@/domain/user'

export function CertificatePage() {
  const { id } = useParams<{ id: string }>()
  const { certificateRepo, users } = useServices()

  const [certificate, setCertificate] = useState<Certificate | null>(null)
  const [learner, setLearner] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!id) return
    certificateRepo.getById(id).then(async (cert) => {
      setCertificate(cert)
      if (cert) {
        const user = await users.getById(cert.userId)
        setLearner(user)
      }
      setLoading(false)
    })
  }, [id, certificateRepo, users])

  if (loading) return <LoadingSpinner label="Loading certificate…" />

  if (!certificate || !learner) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Certificate not found</h1>
        <p className="mt-3 text-white/60">
          This certificate ID doesn't match any record in this browser's local data.
        </p>
        <Link to="/dashboard" className="cyber-btn-primary mt-8 inline-flex">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  const issuedDate = new Date(certificate.issuedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="no-print flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <Link to="/profile" className="text-sm text-white/50 hover:text-white">
          ← Profile
        </Link>
        <div className="flex items-center gap-2">
          {certificate.status === 'revoked' && (
            <span className="cyber-pill bg-coral-500/15 text-coral-500 ring-1 ring-coral-500/30">Revoked</span>
          )}
          <button onClick={() => window.print()} className="cyber-btn-primary">
            Download / Print PDF
          </button>
        </div>
      </div>

      <div
        id="certificate"
        className="cyber-panel relative mt-6 overflow-hidden border-2 border-teal-400/30 p-10 text-center sm:p-14"
      >
        <div className="pointer-events-none absolute inset-0 bg-cyber-grid bg-[length:24px_24px] opacity-10" />
        <div className="relative">
          <img src="/logo-192.png" alt="CyberTales Learning" className="mx-auto h-16 w-16" />
          <p className="mt-4 text-xs font-semibold uppercase tracking-[0.3em] text-amber-400">
            Stories. Skills. Safer Futures.
          </p>
          <h1 className="mt-6 font-display text-lg font-semibold uppercase tracking-[0.2em] text-white/60">
            Certificate of Completion
          </h1>
          <p className="mt-6 text-sm text-white/50">This certifies that</p>
          <p className="mt-2 font-display text-3xl font-bold gradient-text sm:text-4xl">
            {learner.displayName.split(' (')[0]}
          </p>
          <p className="mt-6 text-sm text-white/50">has successfully completed</p>
          <p className="mt-2 font-display text-xl font-semibold text-white">{certificate.courseName}</p>

          <div className="mx-auto mt-10 h-px w-40 bg-gradient-to-r from-transparent via-teal-400/50 to-transparent" />

          <div className="mt-8 flex flex-col items-center justify-center gap-4 text-xs text-white/40 sm:flex-row sm:gap-10">
            <p>Issued {issuedDate}</p>
            <p className="font-mono">Certificate ID: {certificate.id}</p>
          </div>

          {certificate.status === 'revoked' && (
            <p className="mt-6 text-sm font-semibold text-coral-500">
              This certificate has been revoked and is no longer valid.
            </p>
          )}
        </div>
      </div>

      <p className="no-print mt-6 text-center text-xs text-white/40">
        Zero-Cost Development Mode: this is a client-rendered certificate. Production (P5) adds a
        public, unauthenticated verification page and QR code via the CloudSecure Certificate Service.
      </p>
    </div>
  )
}
